import { cur } from '@/lib/currency';
import { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { CalendarClock, ArrowRight, Banknote, AlertCircle, MessageCircle, Settings as SettingsIcon, Building2, ArrowDownCircle, ArrowUpCircle } from 'lucide-react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import api from '@/lib/api';

/**
 * MonthlyCloseoutWidget — iter406.
 *
 * Compact dashboard tile that surfaces the monthly close-out preview
 * the cron would run on the 1st. Helps users:
 *   - See last month's inter-branch settlements + overdue customers
 *     at a glance without waiting until next month
 *   - Click into the Customers page (Bulk-WhatsApp) to act on
 *     overdue receivables before the cron fires
 *
 * Renders a slim placeholder when both metrics are zero (silent month).
 */
const SR = (n) => Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export function MonthlyCloseoutWidget({ testidPrefix = 'closeout-widget' }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [waConfig, setWaConfig] = useState({ enabled: false, owner_phone: '' });
  const [waOpen, setWaOpen] = useState(false);
  const [waSaving, setWaSaving] = useState(false);
  // iter407 — per-branch drill-down state
  const [branchOpen, setBranchOpen] = useState(false);
  const [branchData, setBranchData] = useState(null);
  const [branchLoading, setBranchLoading] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [previewRes, waRes] = await Promise.all([
          api.get('/scheduler/monthly-closeout/preview'),
          api.get('/scheduler/monthly-closeout/whatsapp-config'),
        ]);
        setData(previewRes.data || null);
        if (waRes.data) setWaConfig(waRes.data);
      } catch { setData(null); }
      finally { setLoading(false); }
    })();
  }, []);

  // iter407 — Lazy-load the per-branch payload only when the user opens
  // the drill-down — saves a round-trip on every dashboard mount for
  // tenants who never click into the breakdown.
  const openBranchDrilldown = async () => {
    setBranchOpen(true);
    if (branchData) return;
    setBranchLoading(true);
    try {
      const r = await api.get('/scheduler/monthly-closeout/preview/by-branch');
      setBranchData(r.data || { by_branch: [], totals: {} });
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load per-branch breakdown');
      setBranchData({ by_branch: [], totals: {} });
    } finally {
      setBranchLoading(false);
    }
  };

  const saveWhatsAppConfig = async () => {
    setWaSaving(true);
    try {
      const res = await api.put('/scheduler/monthly-closeout/whatsapp-config', waConfig);
      setWaConfig(res.data?.monthly_closeout_whatsapp || waConfig);
      toast.success(waConfig.enabled
        ? 'WhatsApp digest enabled — link will be in next month\'s notification'
        : 'WhatsApp digest disabled');
      setWaOpen(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save WhatsApp config');
    } finally { setWaSaving(false); }
  };

  if (loading || !data) return null;
  const noActivity = (data.settlements_count || 0) === 0 && (data.overdue_customers || 0) === 0;

  return (
    <Card
      className="border-violet-200/60 bg-gradient-to-br from-violet-50/40 to-fuchsia-50/40 dark:from-violet-950/30 dark:to-fuchsia-950/20"
      data-testid={`${testidPrefix}-card`}
    >
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-semibold flex items-center gap-2">
          <CalendarClock size={14} className="text-violet-600" />
          Monthly close-out preview
          <span className="ml-auto text-[10px] uppercase tracking-wider font-medium text-violet-700 bg-violet-100 dark:bg-violet-900/40 px-2 py-0.5 rounded-full">
            {data.period}
          </span>
          <button
            type="button"
            onClick={() => setWaOpen(true)}
            className="h-6 w-6 rounded-md hover:bg-violet-100 dark:hover:bg-violet-900/40 flex items-center justify-center text-violet-700"
            title={waConfig.enabled ? `WhatsApp digest enabled (${waConfig.owner_phone})` : 'Enable WhatsApp digest'}
            data-testid={`${testidPrefix}-wa-toggle`}
          >
            {waConfig.enabled ? <MessageCircle size={12} className="text-emerald-600" /> : <SettingsIcon size={12} />}
          </button>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {noActivity ? (
          <div className="flex items-center gap-2 text-xs text-muted-foreground py-1" data-testid={`${testidPrefix}-empty`}>
            <AlertCircle size={12} />
            No inter-branch settlements or overdue customers in {data.period} — nothing to close out.
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-2 text-xs">
            <div className="rounded-lg bg-white/70 dark:bg-black/30 border border-border/40 p-2.5" data-testid={`${testidPrefix}-settlements`}>
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Settlements</div>
              <div className="font-bold tabular-nums text-base mt-0.5 flex items-baseline gap-1">
                {data.settlements_count}
                <span className="text-[10px] font-normal text-muted-foreground">posted</span>
              </div>
              {data.settlements_total > 0 && (
                <div className="text-[10px] text-muted-foreground mt-0.5">{cur()} {SR(data.settlements_total)} total</div>
              )}
            </div>
            <div className="rounded-lg bg-white/70 dark:bg-black/30 border border-border/40 p-2.5" data-testid={`${testidPrefix}-overdue`}>
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Overdue customers</div>
              <div className="font-bold tabular-nums text-base mt-0.5 flex items-baseline gap-1">
                {data.overdue_customers}
                <span className="text-[10px] font-normal text-muted-foreground">accounts</span>
              </div>
              {data.overdue_customers > 0 && (
                <div className="text-[10px] text-warning font-medium mt-0.5 flex items-center gap-1">
                  <Banknote size={10} />Needs follow-up
                </div>
              )}
            </div>
          </div>
        )}
        <div className="flex items-center gap-2 pt-1 flex-wrap">
          <span className="text-[10px] text-muted-foreground flex-1 min-w-0">
            Next auto-run: <b className="text-foreground">{data.next_run_at}</b>
          </span>
          {!noActivity && (
            <button
              type="button"
              onClick={openBranchDrilldown}
              className="inline-flex items-center gap-1 text-[11px] font-medium text-violet-700 hover:text-violet-900 px-2 py-1 rounded-md hover:bg-violet-100/60"
              data-testid={`${testidPrefix}-branch-drilldown`}
            >
              <Building2 size={11} />Per-branch breakdown
            </button>
          )}
          {!noActivity && (
            <Button asChild variant="outline" size="sm" className="h-7 text-[11px] px-2.5" data-testid={`${testidPrefix}-cta`}>
              <Link to="/customers">
                Send statements <ArrowRight size={11} className="ml-1" />
              </Link>
            </Button>
          )}
        </div>
      </CardContent>

      {/* iter407 — Per-branch drill-down dialog */}
      <Dialog open={branchOpen} onOpenChange={setBranchOpen}>
        <DialogContent className="max-w-3xl" data-testid={`${testidPrefix}-branch-dialog`}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Building2 size={16} className="text-violet-600" />
              Per-branch close-out · {data?.period}
            </DialogTitle>
            <DialogDescription>
              Inter-branch settlements + overdue receivables, split by branch. The cron-generated digest persists this same breakdown for audit.
            </DialogDescription>
          </DialogHeader>
          {branchLoading ? (
            <div className="py-8 text-center text-xs text-muted-foreground">Loading…</div>
          ) : !branchData || (branchData.by_branch || []).length === 0 ? (
            <div className="py-8 text-center text-xs text-muted-foreground flex flex-col items-center gap-2">
              <AlertCircle size={20} className="text-muted-foreground" />
              No branch had settlements or overdue customers in {data?.period}.
            </div>
          ) : (
            <div className="overflow-x-auto -mx-2 px-2">
              <table className="w-full text-xs" data-testid={`${testidPrefix}-branch-table`}>
                <thead className="text-[10px] uppercase tracking-wider text-muted-foreground border-b">
                  <tr>
                    <th className="text-left py-2 px-2">Branch</th>
                    <th className="text-right py-2 px-2">
                      <span className="inline-flex items-center gap-1"><ArrowDownCircle size={10} className="text-emerald-600" />Received</span>
                    </th>
                    <th className="text-right py-2 px-2">
                      <span className="inline-flex items-center gap-1"><ArrowUpCircle size={10} className="text-rose-600" />Paid out</span>
                    </th>
                    <th className="text-right py-2 px-2">Net</th>
                    <th className="text-right py-2 px-2">Overdue customers</th>
                    <th className="text-right py-2 px-2">Outstanding</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border/40">
                  {(branchData.by_branch || []).map((row) => {
                    const net = (row.settlements_in_total || 0) - (row.settlements_out_total || 0);
                    return (
                      <tr key={row.branch_id} data-testid={`${testidPrefix}-branch-row-${row.branch_id}`}>
                        <td className="py-2 px-2 font-medium">{row.branch_name}</td>
                        <td className="py-2 px-2 text-right tabular-nums">
                          <div>{cur()} {SR(row.settlements_in_total)}</div>
                          {row.settlements_in_count > 0 && (
                            <div className="text-[10px] text-muted-foreground">{row.settlements_in_count} txn</div>
                          )}
                        </td>
                        <td className="py-2 px-2 text-right tabular-nums">
                          <div>{cur()} {SR(row.settlements_out_total)}</div>
                          {row.settlements_out_count > 0 && (
                            <div className="text-[10px] text-muted-foreground">{row.settlements_out_count} txn</div>
                          )}
                        </td>
                        <td className={`py-2 px-2 text-right tabular-nums font-semibold ${net > 0 ? 'text-emerald-700' : net < 0 ? 'text-rose-700' : ''}`}>
                          {net > 0 ? '+' : ''}SR {SR(Math.abs(net))}
                        </td>
                        <td className="py-2 px-2 text-right tabular-nums">{row.overdue_customers || 0}</td>
                        <td className="py-2 px-2 text-right tabular-nums text-warning font-medium">
                          {row.overdue_balance > 0 ? `${cur()} ${SR(row.overdue_balance)}` : '—'}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                {branchData.totals && (
                  <tfoot className="border-t-2 font-semibold">
                    <tr data-testid={`${testidPrefix}-branch-totals`}>
                      <td className="py-2 px-2">Tenant total</td>
                      <td className="py-2 px-2 text-right tabular-nums">{cur()} {SR(branchData.totals.settlements_in_total)}</td>
                      <td className="py-2 px-2 text-right tabular-nums">{cur()} {SR(branchData.totals.settlements_out_total)}</td>
                      <td className="py-2 px-2 text-right tabular-nums">—</td>
                      <td className="py-2 px-2 text-right tabular-nums">{branchData.totals.overdue_customers}</td>
                      <td className="py-2 px-2 text-right tabular-nums text-warning">{cur()} {SR(branchData.totals.overdue_balance)}</td>
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setBranchOpen(false)} data-testid={`${testidPrefix}-branch-close`}>Close</Button>
            <Button asChild className="bg-violet-600 hover:bg-violet-700">
              <Link to="/customers">Send statements <ArrowRight size={11} className="ml-1" /></Link>
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={waOpen} onOpenChange={setWaOpen}>
        <DialogContent className="max-w-md" data-testid={`${testidPrefix}-wa-dialog`}>
          <DialogHeader>
            <DialogTitle>WhatsApp digest opt-in</DialogTitle>
            <DialogDescription>
              When enabled, every monthly close-out notification includes a one-tap WhatsApp share link to the owner's phone — no API integration needed.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={waConfig.enabled}
                onChange={(e) => setWaConfig({ ...waConfig, enabled: e.target.checked })}
                className="rounded"
                data-testid={`${testidPrefix}-wa-enable`}
              />
              <span className="text-sm font-medium">Enable WhatsApp digest</span>
            </label>
            <div>
              <Label className="text-[11px] uppercase tracking-wider text-muted-foreground">Owner phone</Label>
              <Input
                value={waConfig.owner_phone}
                onChange={(e) => setWaConfig({ ...waConfig, owner_phone: e.target.value })}
                placeholder="966512345678 or 0512345678"
                className="mt-1 font-mono"
                disabled={!waConfig.enabled}
                data-testid={`${testidPrefix}-wa-phone`}
              />
              <p className="text-[10px] text-muted-foreground mt-1">
                Saudi numbers auto-prepend +966. Make sure the owner has WhatsApp installed.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setWaOpen(false)} disabled={waSaving}>Cancel</Button>
            <Button
              onClick={saveWhatsAppConfig}
              disabled={waSaving || (waConfig.enabled && !waConfig.owner_phone.trim())}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid={`${testidPrefix}-wa-save`}
            >
              {waSaving ? 'Saving…' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

export default MonthlyCloseoutWidget;
